import { fetchOverallStatus, fetchServices, fetchUptime, fetchIncidents, createSubscription } from './api';

/**
 * Status Page Controller
 * Polls the backend API every minute and updates the UI
 */
class StatusPage {
  constructor(options = {}) {
    this.refreshInterval = options.refreshInterval || 60000; // 1 minute
    this.initialLoadDelay = options.initialLoadDelay || 100; // Small delay for DOM ready
    this.initialized = false;
  }

  /**
   * Initialize the status page
   */
  init() {
    if (this.initialized) return;

    // Wait a moment for DOM to be fully ready
    setTimeout(() => {
      this.setupEventDelegation();
      this.loadAll();
      this.startPolling();
      this.initialized = true;
      console.log('[StatusPage] Initialized');
    }, this.initialLoadDelay);
  }

  /**
   * Setup event delegation for dynamic elements (uptime bars, etc.)
   * This prevents memory leaks from adding listeners on every poll
   */
  setupEventDelegation() {
    document.addEventListener('click', (e) => {
      const bar = e.target.closest('.uptime-bar.clickable');
      if (bar?.dataset.incidentUrl) {
        window.open(bar.dataset.incidentUrl, '_blank', 'noopener,noreferrer');
      }
    });
  }

  /**
   * Start polling for updates
   */
  startPolling() {
    setInterval(() => {
      this.loadAll();
    }, this.refreshInterval);
  }

  /**
   * Load all data and update UI
   */
  async loadAll() {
    try {
      await Promise.all([
        this.loadOverallStatus(),
        this.loadServices(),
        this.loadIncidents()
      ]);
    } catch (error) {
      console.error('[StatusPage] Error loading data:', error);
    }
  }

  /**
   * Load and update overall status banner
   */
  async loadOverallStatus() {
    try {
      const data = await fetchOverallStatus();
      this.updateOverallStatus(data);
    } catch (error) {
      console.error('[StatusPage] Failed to load overall status:', error);
      this.setErrorState();
    }
  }

  /**
   * Load and update all services
   */
  async loadServices() {
    try {
      const data = await fetchServices();
      this.updateServices(data.services);
    } catch (error) {
      console.error('[StatusPage] Failed to load services:', error);
    }
  }

  /**
   * Load and update incidents
   */
  async loadIncidents() {
    try {
      const data = await fetchIncidents();
      this.updateIncidents(data.incidents);
    } catch (error) {
      console.error('[StatusPage] Failed to load incidents:', error);
    }
  }

  /**
   * Update overall status banner
   */
  updateOverallStatus(data) {
    const banner = document.getElementById('page-status');
    if (!banner) return;

    const statusSpan = banner.querySelector('.status');

    // Map API status to CSS class
    const statusClass = this.mapOverallStatusToClass(data.status);

    // Remove old status classes
    banner.className = banner.className.replace(/status-\w+/g, '');
    banner.classList.add(statusClass);

    // Update text
    if (statusSpan && data.message) {
      statusSpan.textContent = data.message;
    }

    // Update page title if needed
    this.updatePageTitle(data.message);
  }

  /**
   * Update all service elements
   */
  updateServices(services) {
    if (!Array.isArray(services)) return;

    services.forEach(service => {
      this.updateServiceElement(service);
    });
  }

  /**
   * Update a single service element
   */
  updateServiceElement(service) {
    // Map slug: solid_queue -> solid-queue for frontend
    const frontendSlug = this.toFrontendSlug(service.slug);
    const element = document.querySelector(`[data-service="${frontendSlug}"]`);

    if (!element) {
      console.warn(`[StatusPage] Service element not found: ${frontendSlug}`);
      return;
    }

    // Update status class
    const statusClass = this.mapServiceStatusToClass(service.status);
    element.className = element.className.replace(/status-\w+/g, '');
    element.classList.add(`status-${statusClass}`);

    // Update status text
    const statusSpan = element.querySelector('.component-status');
    if (statusSpan) {
      statusSpan.textContent = this.formatStatus(service.status);
    }

    // Load and update uptime bars
    this.loadAndRenderUptimeBars(element, service.slug);
  }

  /**
   * Load and render uptime bars for a service
   */
  async loadAndRenderUptimeBars(element, slug) {
    try {
      const data = await fetchUptime(slug);
      this.renderUptimeBars(element, data.bars);
    } catch (error) {
      console.error(`[StatusPage] Failed to load uptime for ${slug}:`, error);
    }
  }

  /**
   * Render uptime bars in the element
   */
  renderUptimeBars(element, bars) {
    if (!Array.isArray(bars)) return;

    const container = element.querySelector('.uptime-bars');
    if (!container) return;

    container.innerHTML = bars.map((bar, index) => {
      const isToday = index === bars.length - 1;
      const date = new Date(bar.date);
      const dateStr = isToday
        ? 'Today'
        : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

      const statusText = this.formatBarStatus(bar.status);
      let title = `${dateStr}: ${statusText}`;

      // Add incident title to tooltip if present
      if (bar.incident?.title) {
        title = `${dateStr}: ${bar.incident.title}`;
      } else if (bar.uptime_percentage) {
        title += ` (${bar.uptime_percentage}%)`;
      }

      const incidentUrl = bar.incident?.url;
      const clickableClass = incidentUrl ? 'clickable' : '';
      const incidentAttr = incidentUrl ? `data-incident-url="${this.escapeHtml(incidentUrl)}"` : '';

      return `<div class="uptime-bar ${bar.status} ${clickableClass}" data-tooltip="${this.escapeHtml(title)}" ${incidentAttr}></div>`;
    }).join('');
  }

  /**
   * Update incidents section
   */
  updateIncidents(incidents) {
    const container = document.querySelector('.incidents-list');
    if (!container) return;

    if (!incidents || incidents.length === 0) {
      container.innerHTML = `
        <div class="incidents-header">
          <h2 class="incidents-title">Past Incidents</h2>
          <a href="/history" class="view-history-link">View history &rarr;</a>
        </div>
        <div class="no-incidents">
          <p>No incidents reported</p>
        </div>
      `;
      return;
    }

    // Build incidents HTML
    const incidentsHtml = incidents.map(incident => {
      const startedAt = new Date(incident.started_at).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      const resolvedAt = incident.resolved_at
        ? new Date(incident.resolved_at).toLocaleString()
        : null;

      const statusClass = incident.status === 'resolved' ? 'resolved' : 'active';

      return `
        <div class="incident-item ${statusClass}">
          <div class="incident-header">
            <h3 class="incident-title">${this.escapeHtml(incident.title)}</h3>
            <span class="incident-status status-${incident.status}">${this.formatStatus(incident.status)}</span>
          </div>
          <div class="incident-meta">
            <span class="incident-date">${startedAt}</span>
            ${incident.impact ? `<span class="incident-impact impact-${incident.impact}">${this.formatStatus(incident.impact)}</span>` : ''}
          </div>
          ${incident.services?.length ? `<div class="incident-services">Affected: ${incident.services.map(s => this.escapeHtml(s)).join(', ')}</div>` : ''}
          ${resolvedAt ? `<div class="incident-resolved">Resolved: ${resolvedAt}</div>` : ''}
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="incidents-header">
        <h2 class="incidents-title">Past Incidents</h2>
        <a href="/history" class="view-history-link">View history &rarr;</a>
      </div>
      <div class="incidents-items">
        ${incidentsHtml}
      </div>
    `;
  }

  /**
   * Set error state when API is unreachable
   */
  setErrorState() {
    const banner = document.getElementById('page-status');
    if (!banner) return;

    banner.className = banner.className.replace(/status-\w+/g, '');
    banner.classList.add('status-critical');

    const statusSpan = banner.querySelector('.status');
    if (statusSpan) {
      statusSpan.textContent = 'Unable to load status';
    }
  }

  /**
   * Update page title with status
   */
  updatePageTitle(statusMessage) {
    if (!statusMessage) return;

    const baseTitle = 'Status - inloop';
    const newTitle = statusMessage === 'All Systems Operational'
      ? baseTitle
      : `${statusMessage} - ${baseTitle}`;

    if (document.title !== newTitle) {
      document.title = newTitle;
    }
  }

  /**
   * Map API overall status to CSS class
   */
  mapOverallStatusToClass(status) {
    const mapping = {
      'none': 'status-none',
      'operational': 'status-none',
      'maintenance': 'status-maintenance',
      'degraded': 'status-minor',
      'minor': 'status-minor',
      'partial': 'status-major',
      'major': 'status-major',
      'critical': 'status-critical'
    };
    return mapping[status] || 'status-none';
  }

  /**
   * Map service status to CSS color class
   */
  mapServiceStatusToClass(status) {
    const mapping = {
      'operational': 'green',
      'degraded': 'yellow',
      'partial_outage': 'yellow',
      'major_outage': 'red',
      'maintenance': 'blue'
    };
    return mapping[status] || 'no-data';
  }

  /**
   * Convert DB slug to frontend slug
   * solid_queue -> solid-queue
   */
  toFrontendSlug(slug) {
    return slug.replace(/_/g, '-');
  }

  /**
   * Format status string for display
   * operational -> Operational
   * major_outage -> Major Outage
   */
  formatStatus(status) {
    if (!status) return 'Unknown';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  }

  /**
   * Format bar status for tooltip
   */
  formatBarStatus(status) {
    const mapping = {
      'up': 'Operational',
      'down': 'Outage',
      'partial': 'Degraded',
      'maintenance': 'Maintenance',
      'no-data': 'No data'
    };
    return mapping[status] || status;
  }

  /**
   * Escape HTML to prevent XSS
   */
  escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.statusPage = new StatusPage();
    window.statusPage.init();
  });
} else {
  window.statusPage = new StatusPage();
  window.statusPage.init();
}

export default StatusPage;

/**
 * Handle subscription form submission
 * @param {Event} event - Form submit event
 * @param {string} type - 'email' or 'webhook'
 */
export async function handleSubscription(event, type) {
  event.preventDefault();

  const submitButton = event.target.querySelector('button[type="submit"]');
  const originalText = submitButton?.textContent || 'Subscribe';

  // Disable button during submission
  if (submitButton) {
    submitButton.disabled = true;
    submitButton.textContent = 'Subscribing...';
  }

  try {
    let params;

    if (type === 'email') {
      const email = document.getElementById('email-input')?.value;
      if (!email) {
        throw new Error('Please enter an email address');
      }
      params = { channel: 'email', email };
    } else if (type === 'webhook') {
      const webhookUrl = document.getElementById('webhook-input')?.value;
      const email = document.getElementById('webhook-email')?.value;
      if (!webhookUrl) {
        throw new Error('Please enter a webhook URL');
      }
      if (!email) {
        throw new Error('Please enter an email address for failure notifications');
      }
      params = { channel: 'webhook', webhook_url: webhookUrl, email };
    } else {
      throw new Error('Invalid subscription type');
    }

    const result = await createSubscription(params);

    // Show success message
    alert(result.message || 'Subscription successful! Please check your email to confirm.');

    // Reset form and close modal
    event.target.reset();
    closeSubscribeModal();

  } catch (error) {
    console.error('[Subscription] Error:', error);
    alert(error.message || 'Failed to subscribe. Please try again.');
  } finally {
    // Re-enable button
    if (submitButton) {
      submitButton.disabled = false;
      submitButton.textContent = originalText;
    }
  }
}

// Expose to global scope for inline handlers
window.handleSubscription = handleSubscription;
